import { Component, OnInit, Inject } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from "@angular/material/dialog"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatIconModule } from "@angular/material/icon"
import { SnackbarService } from "../../../services/snackbar.service"
import {
  StationRatesService,
  CreateStationRateRequest,
} from "../../../services/station-rates.service"
import { Station } from "../../../models/station.model"
import { NoScrollInputDirective } from "../../../directives/no-scroll-input.directive"

interface DialogData {
  station: Station
}

@Component({
  selector: "app-add-station-rate-dialog",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatIconModule,
    NoScrollInputDirective,
  ],
  templateUrl: "./add-station-rate-dialog.component.html",
  styleUrls: ["./add-station-rate-dialog.component.css"],
})
export class AddStationRateDialogComponent implements OnInit {
  loading = false
  pricingType: "cpm" | "ratecard" = "cpm"

  rateData: CreateStationRateRequest = {
    station_id: 0,
    spot_length: 30,
    start_hour: 6,
    end_hour: 18,
    days: {
      mon: true,
      tue: true,
      wed: true,
      thu: true,
      fri: true,
      sat: false,
      sun: false,
    },
    cpm: 5.0,
    auto_generate_all_lengths: false,
  }

  spotLengthOptions = [
    { value: 15, label: "15 seconds" },
    { value: 30, label: "30 seconds" },
    { value: 60, label: "60 seconds" },
  ]

  hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: this.formatHour(i),
  }))

  /**
   * end_hour is INCLUSIVE on the backend (18 = the 6:00–6:59 PM hour; the rates table shows
   * it as ending 7 PM), so each option names the whole hour it covers. Stored values are unchanged.
   */
  endHourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${this.formatHour(i)} – ${this.formatHour((i + 1) % 24)}`,
  }))

  constructor(
    private dialogRef: MatDialogRef<AddStationRateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private stationRatesService: StationRatesService,
    private snackbar: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.rateData.station_id = this.data.station.id
    this.rateData.pricing_type = this.pricingType
  }

  formatHour(hour: number): string {
    if (hour === 0) return "12:00 AM"
    if (hour === 12) return "12:00 PM"
    if (hour < 12) return `${hour}:00 AM`
    return `${hour - 12}:00 PM`
  }

  setPricingType(type: "cpm" | "ratecard"): void {
    if (this.pricingType === type) return
    this.pricingType = type
    this.onPricingTypeChange()
  }

  onPricingTypeChange(): void {
    this.rateData.pricing_type = this.pricingType
    if (this.pricingType === "cpm") {
      this.rateData.cpm = 5.0
      delete this.rateData.ratecard
    } else {
      this.rateData.ratecard = 50.0
      delete this.rateData.cpm
    }
  }

  isFormValid(): boolean {
    const hasValidPricing =
      this.pricingType === "cpm"
        ? this.rateData.cpm !== undefined && this.rateData.cpm > 0
        : this.rateData.ratecard !== undefined && this.rateData.ratecard > 0

    const hasSelectedDays = Object.values(this.rateData.days).some((day) => day)
    const hasValidTimeRange = this.rateData.end_hour >= this.rateData.start_hour

    return hasValidPricing && hasSelectedDays && hasValidTimeRange
  }

  onSubmit(): void {
    if (!this.isFormValid()) {
      this.snackbar.warning("Please fill in all required fields correctly", 3000)
      return
    }

    this.loading = true

    const payload: CreateStationRateRequest = {
      ...this.rateData,
      pricing_type: this.pricingType,
    }
    if (this.pricingType === "cpm") {
      delete payload.ratecard
    } else {
      delete payload.cpm
    }

    this.stationRatesService.createStationRate(payload).subscribe({
      next: () => {
        this.snackbar.success("Station rate created successfully", 3000)
        this.dialogRef.close(true)
      },
      error: (error) => {
        console.error("Error creating station rate:", error)
        const errorMessage = error.error?.error || "Error creating station rate"
        this.snackbar.error(errorMessage, 5000)
        this.loading = false
      },
    })
  }

  onCancel(): void {
    this.dialogRef.close(false)
  }
}
